// LayerLoaderGLB.js
import { Cartesian3, Math as CesiumMath, HeadingPitchRoll, Transforms } from "cesium";
import { createUniqueId } from "../Utility";
import Type from "../Types";

/**
 * Loads a GLB model into Cesium and updates React state.
 * Works with arrayBuffer from file input or fetch.
 * @param {Object} opts
 * @param {ArrayBuffer} opts.arrayBuffer - GLB binary data
 * @param {String} opts.fileName - File name
 * @param {Object} opts.viewerRef - Cesium viewer ref
 * @param {Function} opts.setMapObjects - React setState for mapObjects
 * @param {Object} [opts.positionDegrees={lon:-112.074, lat:33.4484, height:0}] - Optional position
 * @param {Object} [opts.orientationDegrees={heading:135, pitch:0, roll:0}] - Optional orientation
 * @param {Number} [opts.scale=1.0] - Model scale
 */
export async function loadGlbTypeAsync({
  arrayBuffer,
  fileName,
  viewerRef,
  setMapObjects,
  positionDegrees = { lon: -112.074, lat: 33.4484, height: 0 },
  orientationDegrees = { heading: 135, pitch: 0, roll: 0 },
  scale = 1.0,
}) {
  return new Promise((resolve, reject) => {
    try {
      const glbData = new Uint8Array(arrayBuffer);

      // --- Compute placement
      const position = Cartesian3.fromDegrees(positionDegrees.lon, positionDegrees.lat, positionDegrees.height);
      const heading = CesiumMath.toRadians(orientationDegrees.heading);
      const pitch = CesiumMath.toRadians(orientationDegrees.pitch);
      const roll = CesiumMath.toRadians(orientationDegrees.roll);
      const hpr = new HeadingPitchRoll(heading, pitch, roll);
      const orientation = Transforms.headingPitchRollQuaternion(position, hpr);

      // --- Add GLB entity to Cesium
      const entity = viewerRef.current.entities.add({
        position,
        orientation,
        model: {
          uri: glbData,
          scale,
        },
      });

      // --- Update React state
      setMapObjects((prev) => [
        ...prev,
        {
          id: createUniqueId(),
          name: "GLB",
          entities: [entity],
          type: Type.ASSET.GLB,
          file_name: fileName,
          file_type: ".glb",
          visible: true,
        },
      ]);

      // --- Focus on model
      viewerRef.current.zoomTo(entity);

      resolve(entity);
    } catch (e) {
      console.error("loadGlbTypeAsync error:", e);
      reject(e);
    }
  });
}
